import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { buttonClasses, Card, MicroLabel } from "@bht/ui";
import {
  fetchOpenRepairSet,
  fetchRepairSetForTest,
  type RepairSet,
} from "../lib/api.js";
import { repairCardCopy } from "../lib/repair-copy.js";
import { reportFunnel } from "../lib/report-error.js";

/**
 * The unfinished work a practice test created, wherever it is shown.
 *
 * ONE component for the score report and for Home, because the two say the same
 * thing about the same set and a second copy would drift. Where the set comes
 * from is the only difference: the report asks for the set belonging to THAT
 * test, Home asks for whatever is open.
 *
 * Renders nothing while loading, on error, or when there is no set. A test where
 * no skill was missed twice gets no card, and a failed fetch must not wedge an
 * empty box into the page. Plan: docs/PLAN-2026-09-16-post-test-retention.md.
 */
export type RepairCardSource =
  | { kind: "test"; sessionId: string }
  | { kind: "open" };

export function RepairCard({
  source,
  funnelStep,
}: {
  source: RepairCardSource;
  /** Emitted once when a card is actually shown, never on the empty case. */
  funnelStep?: string;
}) {
  // The loaded set is stored WITH the key it was loaded for. React Router reuses
  // this component across /practice-test/report/A to /report/B, and keeping only
  // the set meant B's report went on rendering A's card, with A's numbers and a
  // link to A's questions, until B's request came back. On a slow connection
  // that is long enough to tap.
  const key = source.kind === "test" ? `test:${source.sessionId}` : "open";
  const [loaded, setLoaded] = useState<{ key: string; set: RepairSet | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load =
      source.kind === "test"
        // Branch on the KIND, not on whether the id is truthy. A report reached
        // with a missing :id would otherwise fall through to "whatever set is
        // open" and show an unrelated test's card on that page.
        ? fetchRepairSetForTest(source.sessionId)
        : fetchOpenRepairSet();
    load
      .then((r) => !cancelled && setLoaded({ key, set: r }))
      .catch(() => {
        /* no card rather than an error box: the rest of the page still works */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` IS the source
  }, [key]);

  const set = loaded?.key === key ? loaded.set : null;
  const copy = repairCardCopy(set, source.kind === "test" ? "report" : "home");

  // Impression keyed to the SET, not to a boolean. A boolean stays true across a
  // change from one visible card to another, so the second card would be shown
  // and never counted. Fires only when a student could actually see one: counting
  // renders would fill the denominator with the nothing-to-show case.
  const shownId = set && copy ? set.id : null;
  useEffect(() => {
    if (shownId && funnelStep) reportFunnel(funnelStep);
  }, [shownId, funnelStep]);

  if (!set || !copy) return null;

  return (
    <Card>
      <MicroLabel>{copy.label}</MicroLabel>
      <p
        style={{
          marginTop: 10,
          marginBottom: 0,
          color: "var(--fg-0)",
          fontSize: "var(--t-body)",
          lineHeight: 1.55,
        }}
      >
        {copy.headline}
      </p>
      <p
        style={{
          marginTop: "var(--sp-2)",
          marginBottom: "var(--sp-4)",
          color: "var(--fg-3)",
          fontSize: "var(--t-small)",
          lineHeight: 1.5,
        }}
      >
        {copy.detail}
        {copy.progress ? ` ${copy.progress}.` : ""}
      </p>
      {/* SECONDARY, because the hero above it is the primary action.
          Two filled amber buttons above the fold compete, and a student reading
          a home screen with two equally loud calls has to decide which one the
          product meant. The hero keeps amber; this keeps the card, which is
          what already marks it as work with its own state. */}
      <Link
        to={`/app/repair/${set.id}`}
        className={buttonClasses({ variant: "secondary", full: true })}
      >
        {copy.cta}
      </Link>
    </Card>
  );
}
